import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material';
import { GoogleMap, LoadScript, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';
import ReviewSection from '../components/ReviewSection';

interface Location {
  _id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  difficulty: string;
  amenities: string[];
  parkingInfo: string;
  accessPoints: {
    name: string;
    latitude: number;
    longitude: number;
  }[];
  images: string[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Map configuration
const mapOptions = {
  mapId: process.env.REACT_APP_GOOGLE_MAPS_ID || 'default',
  disableDefaultUI: false,
  clickableIcons: true,
  scrollwheel: true,
};

const LocationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string>('');
  const [mapError, setMapError] = useState<string>('');
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
  };

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        console.log('Fetching location with ID:', id);
        const response = await axios.get(`${API_URL}/api/locations/${id}`);
        console.log('Location data:', response.data);
        setLocation(response.data);
      } catch (error) {
        console.error('Error fetching location:', error);
        setError('Failed to load location details');
      }
    };

    if (id) {
      fetchLocation();
    }
  }, [id]);

  const handleMapError = (error: Error) => {
    console.error('Google Maps Error:', error);
    setMapError('Failed to load Google Maps. Please check your API key and try again.');
  };

  const onMapLoad = (map: google.maps.Map) => {
    setMap(map);
    if (location) {
      map.panTo({ lat: location.latitude, lng: location.longitude });
      map.setZoom(14);
    }
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!location) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ textDecoration: 'none' }}
        >
          Home
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/locations')}
          sx={{ textDecoration: 'none' }}
        >
          Locations
        </Link>
        <Typography color="text.primary">{location.name}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        {location.name}
      </Typography>

      {mapError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mapError}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Map Section */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <LoadScript
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
              onError={handleMapError}
              libraries={['marker']}
            >
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={{ lat: location.latitude, lng: location.longitude }}
                zoom={14}
                onLoad={onMapLoad}
                options={mapOptions}
              >
                <InfoWindow
                  position={{ lat: location.latitude, lng: location.longitude }}
                >
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle1">{location.name}</Typography>
                  </Box>
                </InfoWindow>
              </GoogleMap>
            </LoadScript>
          </Paper>
        </Grid>

        {/* Location Details */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              About this Location
            </Typography>
            <Typography paragraph>{location.description}</Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Difficulty</Typography>
                <Typography variant="body1" color="text.secondary">
                  {location.difficulty.charAt(0).toUpperCase() + location.difficulty.slice(1)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Parking</Typography>
                <Typography variant="body1" color="text.secondary">
                  {location.parkingInfo}
                </Typography>
              </Grid>
            </Grid>

            {location.amenities && location.amenities.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Amenities
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {location.amenities.map((amenity, index) => (
                    <Chip key={index} label={amenity} sx={{ mt: 1 }} />
                  ))}
                </Stack>
              </>
            )}

            {location.accessPoints && location.accessPoints.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Access Points
                </Typography>
                <Stack spacing={2}>
                  {location.accessPoints.map((point, index) => (
                    <Paper key={index} elevation={1} sx={{ p: 2 }}>
                      <Typography variant="subtitle1">{point.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Coordinates: {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Grid>

        {/* Reviews Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <ReviewSection locationId={location._id} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default LocationDetail; 